import type { LiveStatus } from './ports/LiveStream';

/**
 * How often a screen showing live data refetches itself, in milliseconds.
 *
 * Section 7.8 specifies Server Sent Events, with polling as the fallback. The
 * stream was built in section 24 of the review of 2 October 2026: while it is
 * open, an event refetches what changed the moment it changes and the screens
 * poll only once a minute as a safety net; while it is down (no EventSource,
 * lab Wi-Fi dropped, the server restarting) they poll every fifteen seconds,
 * as they always did.
 *
 * Without either, two devices only converge when one of them is refocused or a
 * query happens to remount, which is why a case created at the reception desk
 * did not appear on a phone that already had the queue open in front of it.
 *
 * Not applied to everything. The catalogue is a price list, reports are a
 * snapshot somebody is reading, and the archive is closed cases; polling those
 * would spend a lab phone's battery re-fetching answers that cannot change.
 *
 * TanStack does not poll a hidden tab unless asked to, and it is not asked to
 * here. A phone in a pocket costs nothing, and `refetchOnWindowFocus` already
 * brings it up to date the moment it comes out of one.
 */
export const LIVE_REFETCH_MS = 15_000;

/** The safety net while the stream is open. */
export const STREAM_OPEN_REFETCH_MS = 60_000;

/** The floor board's own pace without the stream: it is the screen left on a wall. */
export const BOARD_REFETCH_MS = 5_000;

let status: LiveStatus = 'down';

export function liveStatus(): LiveStatus {
  return status;
}

export function setLiveStatus(next: LiveStatus): void {
  status = next;
}

/** For `refetchInterval`: read each time a query schedules its next poll. */
export function liveRefetchInterval(): number {
  return status === 'open' ? STREAM_OPEN_REFETCH_MS : LIVE_REFETCH_MS;
}

export function boardRefetchInterval(): number {
  return status === 'open' ? STREAM_OPEN_REFETCH_MS : BOARD_REFETCH_MS;
}
